/**
 * Capacidad estimada (paso 1.2, punto e): cuántos minutos por día pide la demanda a cada recurso y
 * cuántos ofrece la jornada, con los tiempos promedio del proceso. Es un cálculo analítico, sin
 * colas ni variabilidad: sirve para advertir cuando la demanda supera claramente lo que el taller
 * puede atender (por ejemplo, 200 autos al día en 6 elevadores), no para reemplazar a la
 * simulación.
 */

import { mediaTeorica } from '../aleatoriedad/distribuciones';
import type { Espacio } from '../dominio/espacio';
import type { Observacion } from '../dominio/observacion';
import {
  distribucionDe,
  type AtributoDeUnidad,
  type Condicion,
  type DefinicionDeProceso,
  type Etapa,
} from '../dominio/proceso';
import { esHoraValida, horaASegundos } from '../dominio/tiempo';
import type { EstacionEnPlanta } from '../espacial/compilador';
import { lugaresEfectivos } from './proceso';

export type RecursoCapacidad = 'bahia' | 'tecnico' | 'asesor' | 'lavado' | 'recepcion';

export const RECURSOS_CAPACIDAD: readonly RecursoCapacidad[] = [
  'bahia',
  'tecnico',
  'asesor',
  'lavado',
  'recepcion',
];

/** Nombre en plural, para los textos de la interfaz. */
export const NOMBRE_RECURSO_CAPACIDAD: Record<RecursoCapacidad, string> = {
  bahia: 'elevadores',
  tecnico: 'técnicos',
  asesor: 'asesores',
  lavado: 'puestos de lavado',
  recepcion: 'cajones de recepción',
};

export interface CargaDeRecurso {
  recurso: RecursoCapacidad;
  /** Unidades disponibles (elevadores activos, personas, puestos). */
  cantidad: number;
  /** Minutos por día que pide la demanda. */
  requeridoMin: number;
  /** Minutos por día que ofrece la jornada. */
  disponibleMin: number;
  /** Requerido ÷ disponible; `null` si no hay unidades. */
  carga: number | null;
}

export interface EstimacionCapacidad {
  demanda: number;
  /** Recursos que el proceso usa, en orden fijo. */
  recursos: CargaDeRecurso[];
  /** Recurso más cargado (el que limita), si alguno tiene demanda. */
  limitante: CargaDeRecurso | null;
  /** Autos por día que caben con la misma mezcla, según el recurso que limita. */
  autosQueCaben: number | null;
  /** Servicios cuya demanda supera a los elevadores compatibles con ellos. */
  serviciosSinBahia: { tipoUnidad: string; requeridoMin: number; disponibleMin: number }[];
}

/** Umbral de «exigido»: sobre esta carga, la cola y la variabilidad ya pesan mucho. */
export const CARGA_EXIGIDA = 0.85;

function probabilidadAtributo(
  atributos: readonly AtributoDeUnidad[],
  id: string,
  tipo: string,
): number {
  return atributos.find((a) => a.id === id)?.probabilidad.valor[tipo] ?? 0;
}

/** Probabilidad de que la condición se cumpla para un auto de este tipo (atributos independientes). */
export function probabilidadCondicion(
  c: Condicion,
  tipo: string,
  atributos: readonly AtributoDeUnidad[],
): number {
  if ('tipoUnidad' in c) return c.tipoUnidad === tipo ? 1 : 0;
  if ('atributo' in c) {
    const p = probabilidadAtributo(atributos, c.atributo, tipo);
    return c.igual ? p : 1 - p;
  }
  if ('probabilidad' in c) return c.probabilidad;
  if ('ubicacionActual' in c) return 0.5;
  if ('y' in c) return c.y.reduce((a, h) => a * probabilidadCondicion(h, tipo, atributos), 1);
  if ('o' in c)
    return 1 - c.o.reduce((a, h) => a * (1 - probabilidadCondicion(h, tipo, atributos)), 1);
  return 1 - probabilidadCondicion(c.no, tipo, atributos);
}

/**
 * Probabilidad de que un auto de este tipo pase por cada etapa (veces esperadas). Recorre las
 * transiciones en orden: la primera que se cumple gana y «en otro caso» se lleva el resto.
 */
export function visitasEsperadas(proceso: DefinicionDeProceso, tipo: string): Map<string, number> {
  const visitas = new Map<string, number>();
  // Las rutas con límite (retrabajos) se cuentan por OT: el estado es la etapa y las veces que ya
  // se tomó cada una; agotada, se evalúan las siguientes, como en el motor.
  const limitadas = proceso.transiciones.flatMap((t, g) => (t.maximo !== undefined ? [g] : []));
  type Estado = { id: string; veces: number[]; p: number };
  const clave = (id: string, veces: number[]) => `${id}|${veces.join(',')}`;
  let pendiente = new Map<string, Estado>([
    [
      clave(
        proceso.etapaInicial,
        limitadas.map(() => 0),
      ),
      {
        id: proceso.etapaInicial,
        veces: limitadas.map(() => 0),
        p: 1,
      },
    ],
  ]);
  // Propagación por oleadas; un ciclo sin límite (que la validación bloquea) se corta tras unas
  // vueltas.
  for (let vuelta = 0; vuelta < 200 && pendiente.size > 0; vuelta++) {
    const siguiente = new Map<string, Estado>();
    for (const s of pendiente.values()) {
      if (s.p < 1e-9) continue;
      visitas.set(s.id, (visitas.get(s.id) ?? 0) + s.p);
      let resto = 1;
      proceso.transiciones.forEach((t, g) => {
        if (t.de !== s.id || resto <= 0) return;
        const k = limitadas.indexOf(g);
        if (k >= 0 && s.veces[k]! >= t.maximo!) return;
        const q = t.sino ? 1 : t.si ? probabilidadCondicion(t.si, tipo, proceso.atributos) : 0;
        const parte = resto * q;
        resto -= parte;
        if (parte <= 0) return;
        const veces = k >= 0 ? s.veces.map((v, j) => (j === k ? v + 1 : v)) : s.veces;
        const c = clave(t.a, veces);
        const previo = siguiente.get(c);
        if (previo) previo.p += s.p * parte;
        else siguiente.set(c, { id: t.a, veces, p: s.p * parte });
      });
    }
    pendiente = siguiente;
  }
  return visitas;
}

function duracionMedia(e: Etapa, tipo: string): number {
  const d = distribucionDe(e, tipo);
  return d ? mediaTeorica(d) : 0;
}

/** Minutos que cada etapa pide a cada recurso, por auto de un tipo (sin colas). */
export function minutosPorAuto(
  proceso: DefinicionDeProceso,
  tipo: string,
): Record<RecursoCapacidad, number> {
  const r: Record<RecursoCapacidad, number> = {
    bahia: 0,
    tecnico: 0,
    asesor: 0,
    lavado: 0,
    recepcion: 0,
  };
  const lugares = lugaresEfectivos(proceso);
  const etapas = new Map(proceso.etapas.map((e) => [e.id, e]));
  for (const [id, veces] of visitasEsperadas(proceso, tipo)) {
    const e = etapas.get(id);
    if (!e || e.tipo === 'salida') continue;
    let d = duracionMedia(e, tipo);
    // La interrupción (por ejemplo, la espera de repuesto) retiene el lugar y a las personas.
    if (e.interrupcion) {
      const int = etapas.get(e.interrupcion.etapa);
      const q = probabilidadCondicion(e.interrupcion.si, tipo, proceso.atributos);
      if (int) d += q * duracionMedia(int, tipo);
    }
    const donde = lugares.get(id) ?? new Set<string>();
    const fraccionEn = (l: string) => (donde.has(l) ? 1 / donde.size : 0);
    r.bahia += veces * d * fraccionEn('bahia');
    r.lavado += veces * d * fraccionEn('lavado');
    r.recepcion += veces * d * fraccionEn('recepcion');
    for (const p of e.personas) {
      if (p.rol === 'tecnico') r.tecnico += veces * d;
      if (p.rol === 'asesor') r.asesor += veces * d;
    }
  }
  return r;
}

/** Autos por día que pide la demanda (en modo agenda, las citas del primer día). */
export function demandaDiaria(espacio: Espacio): number {
  const d = espacio.modelo.demanda;
  if (d.modo === 'agenda') return (d.agenda ?? []).filter((c) => c.dia === 1).length;
  return d.unidadesPorDia.valor;
}

export function estimarCapacidad(
  espacio: Espacio,
  estaciones: readonly EstacionEnPlanta[],
): EstimacionCapacidad {
  const m = espacio.modelo;
  const proceso = m.proceso;
  const demanda = demandaDiaria(espacio);
  const jornadaValida =
    esHoraValida(m.jornada.inicioTurno.valor) &&
    esHoraValida(m.jornada.finTurno.valor) &&
    esHoraValida(m.jornada.cierre.valor);
  const turnoMin = jornadaValida
    ? Math.max(
        0,
        horaASegundos(m.jornada.finTurno.valor) - horaASegundos(m.jornada.inicioTurno.valor),
      ) / 60
    : 0;
  const lavadoMin =
    jornadaValida && m.politicas.lavadoDespuesDeHora
      ? Math.max(
          0,
          horaASegundos(m.jornada.cierre.valor) - horaASegundos(m.jornada.inicioTurno.valor),
        ) / 60
      : turnoMin;

  // El refrigerio de los técnicos descuenta su duración de la jornada de cada técnico.
  const refrigerioMin = m.refrigerio ? Math.max(0, m.refrigerio.duracionMin.valor) : 0;
  const tecnicoMin = Math.max(0, turnoMin - refrigerioMin);

  const mezcla = m.demanda.mezcla.valor;
  const porTipo = new Map(proceso.tiposDeUnidad.map((t) => [t.id, minutosPorAuto(proceso, t.id)]));
  const autosPorTipo = (tipo: string) =>
    m.demanda.modo === 'agenda'
      ? (m.demanda.agenda ?? []).filter((c) => c.dia === 1 && c.tipoUnidad === tipo).length
      : demanda * (mezcla[tipo] ?? 0);

  const bahias = estaciones.filter((s) => s.tipo === 'bahia');
  const cantidad: Record<RecursoCapacidad, number> = {
    bahia: bahias.length,
    tecnico: m.dotacion.tecnico?.valor ?? 0,
    asesor: m.dotacion.asesor?.valor ?? 0,
    lavado: estaciones.filter((s) => s.tipo === 'lavado').length,
    recepcion: estaciones.filter((s) => s.tipo === 'recepcion').length,
  };
  const recursos: CargaDeRecurso[] = [];
  for (const recurso of RECURSOS_CAPACIDAD) {
    let requerido = 0;
    for (const [tipo, mins] of porTipo) requerido += autosPorTipo(tipo) * mins[recurso];
    if (requerido <= 0) continue;
    const disponible =
      cantidad[recurso] *
      (recurso === 'lavado' ? lavadoMin : recurso === 'tecnico' ? tecnicoMin : turnoMin);
    recursos.push({
      recurso,
      cantidad: cantidad[recurso],
      requeridoMin: requerido,
      disponibleMin: disponible,
      carga: disponible > 0 ? requerido / disponible : null,
    });
  }
  const limitante =
    recursos.reduce<CargaDeRecurso | null>(
      (peor, c) => (peor === null || (c.carga ?? Infinity) > (peor.carga ?? Infinity) ? c : peor),
      null,
    ) ?? null;
  const autosQueCaben =
    limitante && limitante.carga !== null && limitante.carga > 0 && demanda > 0
      ? Math.floor(demanda / limitante.carga)
      : null;

  // Cada servicio solo puede usar los elevadores compatibles con él.
  const serviciosSinBahia: EstimacionCapacidad['serviciosSinBahia'] = [];
  for (const [tipo, mins] of porTipo) {
    const requerido = autosPorTipo(tipo) * mins.bahia;
    if (requerido <= 0) continue;
    const compatibles = bahias.filter(
      (b) => b.compatibles === null || b.compatibles.includes(tipo),
    );
    const disponible = compatibles.length * turnoMin;
    if (disponible > 0 && requerido > disponible)
      serviciosSinBahia.push({
        tipoUnidad: tipo,
        requeridoMin: requerido,
        disponibleMin: disponible,
      });
  }
  return { demanda, recursos, limitante, autosQueCaben, serviciosSinBahia };
}

const horas = (min: number) => (Math.round(min / 6) / 10).toFixed(1);
const porcentaje = (f: number) => `${Math.round(f * 100)} %`;

/** Advertencias de capacidad: solo cuando la demanda supera lo que el recurso puede atender. */
export function observacionesDeCapacidad(
  espacio: Espacio,
  est: EstimacionCapacidad,
): Observacion[] {
  const r: Observacion[] = [];
  const nombreTipo = (id: string) =>
    espacio.modelo.proceso.tiposDeUnidad.find((t) => t.id === id)?.nombre ?? id;
  for (const c of est.recursos) {
    if (c.carga === null || c.carga <= 1) continue;
    const nombre = NOMBRE_RECURSO_CAPACIDAD[c.recurso];
    const caben = est.autosQueCaben;
    r.push({
      regla: 'capacidad-insuficiente',
      nivel: 'advierte',
      que: `La demanda supera la capacidad estimada de los ${nombre} (${porcentaje(c.carga)})`,
      donde: 'Parámetros · Demanda',
      porque: `Con los tiempos promedio, ${est.demanda} autos al día piden ≈ ${horas(c.requeridoMin)} h de ${nombre} y la jornada ofrece ${horas(c.disponibleMin)} h (${c.cantidad} ${nombre}). ${
        caben !== null && c === est.limitante
          ? `Con esta mezcla caben ≈ ${caben} autos al día: el resto quedará sin atender o pendiente al cierre.`
          : 'Parte de los autos quedará sin atender o pendiente al cierre.'
      }`,
      queHacer:
        caben !== null && c === est.limitante
          ? `Baja la demanda a ≈ ${caben} autos al día o agrega ${nombre}. Puedes simular igual para ver dónde se atasca.`
          : `Agrega ${nombre} o revisa los tiempos. Puedes simular igual para ver dónde se atasca.`,
      elementos: [],
    });
  }
  for (const s of est.serviciosSinBahia) {
    if (r.some((o) => o.que.includes('elevadores'))) break;
    r.push({
      regla: 'capacidad-servicio',
      nivel: 'advierte',
      que: `Los elevadores que atienden «${nombreTipo(s.tipoUnidad)}» no alcanzan (${porcentaje(s.requeridoMin / s.disponibleMin)})`,
      donde: 'Elevadores y Parámetros · Demanda',
      porque: `«${nombreTipo(s.tipoUnidad)}» pide ≈ ${horas(s.requeridoMin)} h de elevador al día y sus elevadores compatibles ofrecen ${horas(s.disponibleMin)} h.`,
      queHacer:
        'Cambia el tipo de algún elevador, marca en «Servicios» otros tipos de elevador para ese servicio o baja su parte de la mezcla.',
      elementos: [],
    });
  }
  return r;
}
